import { DownloadIcon, EyeIcon, PDFIcon } from '@/components';
import { ClipboardList, Copy, MapPin } from 'lucide-react';
import {
  DRIVER,
  LOCATION,
  ORDER_ACTIONS,
  ORDER_STATUS,
  PICKUP,
  ROUTE,
} from '@/config';
import { ColumnsType } from 'antd/es/table';
import { Dropdown, Flex, message } from 'antd';
import { ITable } from '@/components/TableAction/TableAction';
import { PermissionName } from '@/types';
import { downloadFile, formatPhoneDisplay} from '@/utils';
import { fetchLabelRTO_CSV, fetchLabelRTO_PDF, fetchRTOProof } from '@/api';
import { useNavigateWithPrevPageState } from '@/hooks/navigateWithPrevPageState';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';

export interface RTOData extends ITable {
  pickupTime: string;
  pickupLocation: string;
  location: string;
  platform_code: string;
  driver: {
    phone: string;
    name?: string;
    assign?: boolean;
  };
  status: string;
  driver_tasks_Icon: string;
}

// Exact rgba status colors for RTO statuses — sourced from getComputedStyle
const RTO_STATUS_HSL: Record<string, { bg: string; text: string }> = {
  'new':                { bg: 'rgba(168,85,247,0.12)',  text: 'rgb(147,51,234)' },
  'scheduled':          { bg: 'rgba(66,140,215,0.12)',  text: 'rgb(43,107,171)' },
  'rto-scheduled':      { bg: 'rgba(66,140,215,0.12)',  text: 'rgb(43,107,171)' },
  'collecting':         { bg: 'rgba(226,140,54,0.12)',  text: 'rgb(182,107,32)' },
  'out-for-collecting': { bg: 'rgba(226,140,54,0.12)',  text: 'rgb(182,107,32)' },
  'out-for-collection': { bg: 'rgba(226,140,54,0.12)',  text: 'rgb(182,107,32)' },
  'out-for-delivery':   { bg: 'rgba(226,140,54,0.12)',  text: 'rgb(182,107,32)' },
  'collected':          { bg: 'rgba(49,196,127,0.12)',  text: 'rgb(39,155,101)' },
  'bulk-collected':     { bg: 'rgba(49,196,127,0.12)',  text: 'rgb(39,155,101)' },
  'delivered':          { bg: 'rgba(49,196,127,0.12)',  text: 'rgb(39,155,101)' },
  'cancelled':          { bg: 'rgba(239,68,68,0.12)',   text: 'rgb(220,38,38)' },
};

const getInitials = (name?: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const nameToColor = (name?: string): string => {
  if (!name) return '#9CA3AF';
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 55%, 45%)`;
};

export const getRTOColumns = (
  openEntityTicketModal: (platform_code?: string) => void,
  hasPermission: (...permissions: string[]) => boolean,
) => {
  const navigateWithPrevPageState = useNavigateWithPrevPageState();
  const { t } = useTranslation();

  const columns: ColumnsType<RTOData> = [
    {
      title: String(t('platform_code')),
      dataIndex: PICKUP,
      key: PICKUP,
      render: (_, record) => (
        <div className='flex flex-col gap-0.5'>
          <div className='flex items-center gap-1.5'>
            <span
              style={{
                fontSize: '13px',
                fontWeight: 700,
                fontFamily: 'monospace',
                color: 'rgb(57,57,60)',
              }}
            >
              {record.platform_code ?? '---'}
            </span>
            {record.platform_code && (
              <Copy
                size={12}
                className='cursor-pointer text-gray-400 hover:text-gray-600 shrink-0'
                onClick={() => {
                  navigator.clipboard.writeText(record.platform_code ?? '');
                  message.success(String(t('copied')));
                }}
              />
            )}
          </div>
          <span style={{ fontSize: '11px', color: 'rgb(115,123,140)' }}>{record.pickupTime}</span>
        </div>
      ),
    },
    {
      title: String(t('drop_off_location')),
      dataIndex: LOCATION,
      key: LOCATION,
      render: (text: string) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <MapPin size={14} color='rgba(115,123,140,0.6)' />
          <span style={{ fontSize: '12px', fontWeight: 400, color: 'rgb(115,123,140)' }}>{text ?? '---'}</span>
        </span>
      ),
    },
    {
      title: String(t('driver')),
      dataIndex: DRIVER,
      key: DRIVER,
      render: (record: { name?: string; phone?: string }, fullRow: RTOData) => {
        const hasDriver = Boolean(record?.name?.trim());
        const rowStatus = (fullRow?.status || '').toLowerCase();
        // Show "assigning driver" animation only for rto-scheduled; otherwise show plain circle
        const showAssigningAnimation = !hasDriver && rowStatus === 'rto-scheduled';

        if (!hasDriver) {
          if (showAssigningAnimation) {
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ position: 'relative', width: 32, height: 32, flexShrink: 0 }}>
                  <div
                    style={{
                      position: 'absolute',
                      inset: -3,
                      borderRadius: '50%',
                      border: '2px solid transparent',
                      borderTopColor: '#EBDD4C',
                      borderRightColor: 'rgba(235,221,76,0.25)',
                      animation: 'boxy-orbit 1.4s linear infinite',
                    }}
                  />
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #F3F4F6 0%, #E9EAEC 100%)',
                      overflow: 'hidden',
                      position: 'relative',
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.65) 50%, transparent 100%)',
                        animation: 'boxy-shimmer 1.8s ease-in-out infinite',
                      }}
                    />
                  </div>
                </div>
                <div className='flex flex-col gap-1.5' style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    {[0, 0.22, 0.44].map((delay, i) => (
                      <div
                        key={i}
                        style={{
                          width: 4,
                          height: 4,
                          borderRadius: '50%',
                          background: '#EBDD4C',
                          animation: `boxy-pulse-dot 1.2s ease-in-out ${delay}s infinite`,
                        }}
                      />
                    ))}
                    <span style={{ fontSize: 10, fontWeight: 500, color: '#9CA3AF', letterSpacing: '0.03em', textTransform: 'uppercase' }}>
                      {String(t('assigning_driver'))}
                    </span>
                  </div>
                </div>
              </div>
            );
          }
          // Non-scheduled unassigned: plain brown circle + blank
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: '#D2B48C',
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: '13px', color: 'rgb(115,123,140)' }}>---</span>
            </div>
          );
        }

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: nameToColor(record?.name),
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {getInitials(record?.name)}
            </div>
            <div className='flex flex-col gap-0.5'>
              <span style={{ fontSize: '13px', fontWeight: 500, color: 'rgb(57,57,60)' }}>
                {record.name!.trim()}
              </span>
              <span style={{ fontSize: '11px', fontWeight: 400, color: 'rgb(115,123,140)' }}>
                {formatPhoneDisplay(record?.phone) || '---'}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: String(t('status')),
      dataIndex: ORDER_STATUS,
      key: ORDER_STATUS,
      render: (text: string) => {
        if (!text) return <span style={{ color: 'rgb(115,123,140)' }}>---</span>;
        const slug = text.toLowerCase();
        const hsl = RTO_STATUS_HSL[slug] ?? {
          bg: 'hsla(0,0%,60%,0.10)',
          text: 'hsl(0,0%,38%)',
        };
        return (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              fontSize: 11,
              fontWeight: 600,
              color: hsl.text,
              backgroundColor: hsl.bg,
              padding: '4px 10px',
              borderRadius: 9999,
              lineHeight: '16.5px',
              whiteSpace: 'nowrap',
            }}
          >
            {String(t(`merchant_rto_status.${slug}`))}
          </span>
        );
      },
    },
    {
      title: String(t('actions')),
      key: ORDER_ACTIONS,
      render: (record: { key?: string; platform_code?: string; status?: string }) => (
        <RTOAction
          id={record.key}
          platformCode={record.platform_code}
          status={record.status}
          openEntityTicketModal={openEntityTicketModal}
          hasPermission={hasPermission}
          navigateWithPrevPageState={navigateWithPrevPageState}
          t={t}
        />
      ),
      width: '10%',
      fixed: 'right' as 'right' | undefined,
    },
  ];

  return columns;
};

const RTOAction = ({
  id,
  platformCode,
  status,
  openEntityTicketModal,
  hasPermission,
  navigateWithPrevPageState,
  t,
}: {
  id: string | undefined;
  platformCode: string | undefined;
  status: string | undefined;
  openEntityTicketModal: (platform_code?: string) => void;
  hasPermission: (...permissions: string[]) => boolean;
  navigateWithPrevPageState: (path: string) => void;
  t: (key: string) => string;
}) => {
  const isDelivered = status === 'delivered' || status === 'rto-delivered';

  const moreMenuItems = [
    {
      key: 'view',
      label: (
        <span className='flex items-center gap-2 text-[13px]'>
          <EyeIcon stroke='#939496' fill='#939496' />
          {String(t('view'))}
        </span>
      ),
      onClick: () =>
        navigateWithPrevPageState(generatePath(ROUTE.PICKUP_RTO_DETAILS, { id: id ?? '' })),
    },
    ...(isDelivered
      ? [
          {
            key: 'download_proof',
            label: (
              <span className='flex items-center gap-2 text-[13px]'>
                <PDFIcon />
                {String(t('download_rto_proof'))}
              </span>
            ),
            onClick: async () => {
              await downloadFile(id ?? '', fetchRTOProof, `RTO_Proof_${id}.pdf`);
            },
          },
        ]
      : []),
    {
      key: 'download_csv',
      label: (
        <span className='flex items-center gap-2 text-[13px]'>
          <DownloadIcon color='#939496' />
          {String(t('download_rto_csv'))}
        </span>
      ),
      onClick: async () => {
        await downloadFile(id ?? '', fetchLabelRTO_CSV, `RTO_${id}.csv`);
      },
    },
  ];

  return (
    <Flex gap={6} align='center' wrap='nowrap'>
      <button
        type='button'
        className='flex items-center gap-1.5 h-7 px-2.5 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[11px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(230,217,60)] transition-colors'
        onClick={() => openEntityTicketModal(platformCode)}
        disabled={!hasPermission(PermissionName.CREATE_TICKET)}
      >
        <ClipboardList size={13} />
        {String(t('ticket'))}
      </button>
      <button
        type='button'
        className='flex items-center gap-1.5 h-7 px-2.5 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[11px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(230,217,60)] transition-colors'
        onClick={async () => {
          await downloadFile(id ?? '', fetchLabelRTO_PDF, `RTO_Statement_${id}.pdf`);
        }}
      >
        <DownloadIcon width={13} height={13} />
        {String(t('statement'))}
      </button>
      <Dropdown menu={{ items: moreMenuItems }} trigger={['click']} placement='bottomRight'>
        <button
          type='button'
          className='flex items-center justify-center w-8 h-8 rounded-lg text-[rgb(115,123,140)] cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors border border-solid border-[rgb(232,230,227)] bg-white'
        >
          <span style={{ fontSize: '16px', lineHeight: 1, letterSpacing: '0.05em' }}>⋯</span>
        </button>
      </Dropdown>
    </Flex>
  );
};
